const STORAGE_KEY = "jooti_analytics_session_id";
const DEDUPE_KEY = "jooti_analytics_dedupe_map";

const DEDUPE_WINDOWS = {
  page_view: 30,
  product_view: 300,
  product_click: 10,
  add_to_cart: 3,
  checkout_start: 300,
  payment_start: 300,
  payment_success: 86400,
  payment_failed: 300,
  search: 10,
  category_view: 60
};

const getSessionId = () => {
  let sessionId = localStorage.getItem(STORAGE_KEY);

  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem(STORAGE_KEY, sessionId);
  }

  return sessionId;
};

const getToken = () => {
  try {
    return localStorage.getItem("userToken") || localStorage.getItem("token") || "";
  } catch {
    return "";
  }
};

const readDedupeMap = () => {
  try {
    return JSON.parse(sessionStorage.getItem(DEDUPE_KEY) || "{}") || {};
  } catch {
    return {};
  }
};

const writeDedupeMap = (map) => {
  try {
    const now = Date.now();
    const cleaned = Object.fromEntries(
      Object.entries(map || {}).filter(([, value]) => now - Number(value || 0) < 24 * 60 * 60 * 1000)
    );

    sessionStorage.setItem(DEDUPE_KEY, JSON.stringify(cleaned));
  } catch {
    return null;
  }
};

const normalizedUrl = () => {
  try {
    return `${window.location.origin}${window.location.pathname}${window.location.search}`;
  } catch {
    return "";
  }
};

const metadataOrderRef = (payload) =>
  payload?.metadata?.order_ref || payload?.metadata?.order_id || payload?.metadata?.order_number || "";

const dedupeKey = (eventType, payload = {}) => {
  const productId = payload.product_id || "";
  const categoryId = payload.category_id || "";
  const searchQuery = String(payload.search_query || "").trim().toLowerCase();
  const orderRef = metadataOrderRef(payload);

  if (eventType === "page_view") {
    return `${eventType}|${normalizedUrl()}`;
  }

  if (eventType === "product_view" || eventType === "product_click") {
    return `${eventType}|product:${productId || normalizedUrl()}`;
  }

  if (eventType === "add_to_cart") {
    const meta = payload.metadata || {};
    return `${eventType}|product:${productId}|${meta.color || ""}|${meta.size || ""}`;
  }

  if (eventType === "category_view") {
    return `${eventType}|category:${categoryId || normalizedUrl()}`;
  }

  if (eventType === "search") {
    return `${eventType}|search:${searchQuery}`;
  }

  if (eventType === "checkout_start") {
    return `${eventType}`;
  }

  if (eventType === "payment_start" || eventType === "payment_success" || eventType === "payment_failed") {
    return `${eventType}|order:${orderRef || normalizedUrl()}`;
  }

  return `${eventType}|${normalizedUrl()}`;
};

const shouldSkipDuplicate = (eventType, payload = {}) => {
  const windowSeconds = DEDUPE_WINDOWS[eventType] || 0;

  if (!windowSeconds) {
    return false;
  }

  const key = dedupeKey(eventType, payload);
  const map = readDedupeMap();
  const now = Date.now();
  const lastTime = Number(map[key] || 0);

  if (lastTime && now - lastTime < windowSeconds * 1000) {
    return true;
  }

  map[key] = now;
  writeDedupeMap(map);

  return false;
};

export const trackAnalytics = async (eventType, payload = {}) => {
  try {
    if (shouldSkipDuplicate(eventType, payload)) {
      return null;
    }

    const token = getToken();

    const body = {
      session_id: getSessionId(),
      event_type: eventType,
      url: window.location.href,
      page_title: document.title,
      referrer: document.referrer || "",
      ...payload
    };

    await fetch("/api/analytics/events", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(body),
      keepalive: true
    });
  } catch {
    return null;
  }
};
